import { forkJoin, Observable, of } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map } from 'rxjs/operators';
import { Store } from '@ngrx/store';

import { DEFAULT_PAGINATION_PARAMS } from '@app/lib/constants/http.constants';
import { EnvironmentService } from '@app/environments/services/environment.service';
import { IFilterParam, IHttpParams, IListNavigator } from '@app/lib/interfaces';
import { IResearchStudy, IResearchStudyDisplay, ITherapeuticArea } from 'projects/labcorp/clinical-trials-ui/src/lib/interfaces';
import { IStudiesState } from '../state/studies.reducer';
import * as studiesSelectors from '../state/studies.selectors';
import * as StudiesActions from '../state/studies.actions';
import * as utils from '@app/lib/utils';

@Injectable({
  providedIn: 'root'
})
export class StudiesService {

  apiUrl: string;
  allOrganizations$: Observable<fhir4.Organization[]>;
  researchStudies$: Observable<IResearchStudyDisplay[]>;
  researchStudyToEdit$: Observable<IResearchStudyDisplay>;
  studyListNavigator$: Observable<IListNavigator>;
  therapeuticAreas$: Observable<ITherapeuticArea[]>;

  constructor(
    private readonly environmentService: EnvironmentService,
    private readonly httpClient: HttpClient,
    private readonly store: Store<IStudiesState>,
  ) {
    this.apiUrl = this.environmentService.environment.restAPI;
    this.allOrganizations$ = this.store.select(studiesSelectors.allOrganizations);
    this.researchStudies$ = this.store.select(studiesSelectors.researchStudies);
    this.researchStudyToEdit$ = this.store.select(studiesSelectors.researchStudyToEdit);
    this.studyListNavigator$ = this.store.select(studiesSelectors.studyListNavigator);
    this.therapeuticAreas$ = this.store.select(studiesSelectors.therapeuticAreas);
  }

  clearResearchStudyState(): void {
    this.store.dispatch(StudiesActions.clearResearchStudyState());
  }

  clearResearchStudyToEdit(): void {
    this.store.dispatch(StudiesActions.clearResearchStudyToEdit());
  }

  _createGroup(group: fhir4.Group): Observable<fhir4.Group> {
    return this.httpClient.post<fhir4.Group>(`${ this.apiUrl }/Group`, group);
  }

  createStudy(researchStudyData: IResearchStudy, groupData: fhir4.Group): void {
    this.store.dispatch(StudiesActions.createResearchStudy({ researchStudyData, groupData }));
  }

  _createStudy(researchStudy: IResearchStudy): Observable<IResearchStudy> {
    return this.httpClient.post<IResearchStudy>(`${ this.apiUrl }/ResearchStudy`, researchStudy);
  }

  _getAllOrganizations(): Observable<fhir4.Organization[]> {
    return this.httpClient
                .get<fhir4.Bundle>(`${ this.apiUrl }/Organization`)
                .pipe(
                  map((bundle: fhir4.Bundle) => {
                    return bundle.entry.map(entry => entry.resource) as fhir4.Organization[] || [];
                  }),
                );
  }

  _getResearchStudyGroup(study: IResearchStudy): Observable<fhir4.Group> {
      return this.httpClient.get<fhir4.Group>(`${ this.apiUrl }/${ study.recruitment?.eligibility?.reference }`);
  }

  getResearchStudies(): void {
    this.store.dispatch(StudiesActions.getResearchStudies());
  }

  _getPaginatedStudies(url: string, params: HttpParams): Observable<IResearchStudyDisplay[]> {
    return this.httpClient
                .get<fhir4.Bundle>(url, { params })
                .pipe(
                  map((bundle: fhir4.Bundle) => {
                    if (bundle.entry) {
                      const researchStudies: IResearchStudyDisplay[] = bundle.entry.map(entry => {
                        if (entry.resource.resourceType.toLowerCase() === 'researchstudy') {
                          const researchStudy: IResearchStudy = entry.resource as IResearchStudy;
                          const organizationIDs: string[] = researchStudy.associatedParty?.map(associatedParty => associatedParty.party.reference.split('/')[1]);
                          const organizations: fhir4.Organization[] = [];
                          const therapeuticAreas: string[] = researchStudy.category?.map(item => item.coding[0].code);
                          bundle.entry.forEach(obj => {
                            if (obj.resource.resourceType.toLowerCase() === 'organization' && organizationIDs?.includes(obj.resource.id)) {
                              organizations.push(obj.resource as fhir4.Organization);
                            }
                          });
                          const studyDisplay: IResearchStudyDisplay = {
                            organizations,
                            researchStudy,
                            therapeuticAreas
                          };

                          return studyDisplay;
                        }

                        return null;
                      }).filter(subject => Boolean(subject));
                      return researchStudies;
                    }
                    return [];
                  })
                );
  }

  _getResearchStudies(params: IHttpParams): Observable<{researchStudies: IResearchStudyDisplay[], total: number}> {
    if (params?.status) {
      const url = `${ this.apiUrl }/ResearchStudy`;
      const httpParams: HttpParams = utils.objectToHttpParams<IHttpParams>({
          ...params
      });
      const defaultParams: HttpParams = utils.objectToHttpParams<IHttpParams>({
        ...params,
        ...DEFAULT_PAGINATION_PARAMS,
      });

      const subscribeables = [
        this._getPaginatedStudies(url, httpParams),
        this._getStudiesTotal(url, defaultParams)
      ];

      return forkJoin(subscribeables)
              .pipe(
                map(res => {
                  const researchStudies: IResearchStudyDisplay[] = res[0] as IResearchStudyDisplay[];
                  const total: number = res[1] as number;

                  return {
                    researchStudies,
                    total
                  };
                })
              );
    }
    else {
      return of({
        researchStudies: [],
        total: 0
      });
    }

  }

  _getStudiesTotal(url: string, params: HttpParams): Observable<number> {
    return this.httpClient
                .get<fhir4.Bundle>(url, { params })
                .pipe(
                  map((bundle: fhir4.Bundle) => {
                    return bundle.total;
                  })
                );
  }

  getTherapeuticAreas(): void {
    this.store.dispatch(StudiesActions.getTherapeuticAreas());
  }

  _getTherapeuticAreas(): Observable<ITherapeuticArea[]> {
    return this.httpClient
                .get<fhir4.ValueSet>(`${ this.apiUrl }/ValueSet/covance-therapeutic-area-value-set`)
                .pipe(
                  map((valueSet: fhir4.ValueSet) => {
                    const therapeuticAreasData: ITherapeuticArea[] = [];
                    valueSet.compose?.include[0]?.concept.forEach(item => {
                      therapeuticAreasData.push(item as ITherapeuticArea);
                    });
                    return therapeuticAreasData;
                  })
                );
  }

  markResearchStudyToEdit(researchStudy: IResearchStudyDisplay): void {
    this.store.dispatch(StudiesActions.markResearchStudyToEdit({ researchStudy }));
  }

  setStudyListPageIndex(index: number): void {
    this.store.dispatch(StudiesActions.setPageIndex({ index }));
  }

  updateEditedResearchStudyAndGroup(researchStudyData: IResearchStudy, groupData: fhir4.Group): void {
    this.store.dispatch(StudiesActions.updateEditedResearchStudy({ researchStudyData, groupData }));
  }

  _updateEditedResearchStudy(researchStudy: IResearchStudy): Observable<IResearchStudy> {
    return this.httpClient.put<IResearchStudy>(`${ this.apiUrl }/ResearchStudy/${ researchStudy.id }`, researchStudy);
  }

  _updateEditedGroup(group: fhir4.Group): Observable<fhir4.Group> {
    return this.httpClient.put<fhir4.Group>(`${ this.apiUrl }/Group/${ group.id }`, group);
  }

  _updateEditedResearchStudyAndGroup(researchStudyData: IResearchStudy, groupData: fhir4.Group): Observable<{researchStudy: IResearchStudy, group: fhir4.Group}> {

    const subscribables = [
      this._updateEditedResearchStudy(researchStudyData),
      this._updateEditedGroup(groupData),
    ];

    return forkJoin(subscribables)
            .pipe(
              map(res => ({
                  researchStudy: res[0] as IResearchStudy,
                  group: res[1] as fhir4.Group
              }))
            );
  }

  updateFilterParams(filterParam: IFilterParam): void {
    this.store.dispatch(StudiesActions.updateFilterParams({ filterParam }));
  }
}
